import { useEffect, useRef, useState } from 'react'
import { Search, Check, ExternalLink, Plus } from 'lucide-react'

/**
 * Editor for `type: lens-visualization` — the visualization a « Visualisation »
 * block shows: one of the visualizations PUBLISHED from Lens
 * (`GET /api/orbit/lens/visualizations?q=`).
 *
 * Value shape: `{nid, label}`.
 *
 * Editing the visualization itself (its query, its chart) happens in Lens, in a
 * NEW window (`/lens?visualization=<nid>`): the block only points to it, so a
 * re-publication in Lens updates every page that shows it.
 */
export const LensVisualizationEditor = ({ value, onSave, onClose }) => {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [selected, setSelected] = useState(value && typeof value === 'object' && value.nid ? value : null)
  const debounce = useRef(null)

  useEffect(() => {
    clearTimeout(debounce.current)
    debounce.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(`/api/orbit/lens/visualizations?q=${encodeURIComponent(q)}`, { credentials: 'include' })
        if (!res.ok) throw new Error(res.status === 403 ? 'Accès refusé aux visualisations.' : `HTTP ${res.status}`)
        setResults((await res.json()).visualizations || [])
        setError('')
      }
      catch (e) {
        setResults([])
        setError(e.message)
      }
      finally {
        setLoading(false)
      }
    }, 200)
    return () => clearTimeout(debounce.current)
  }, [q])

  const current = results.find((r) => r.id === selected?.nid)

  return (
    <div className="flex flex-col gap-3">
      <label className="relative block">
        <span className="sr-only">Rechercher une visualisation publiée</span>
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input
          type="search"
          className="input w-full pl-9"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher une visualisation publiée…"
          autoFocus
        />
      </label>

      <ul className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto" aria-busy={loading}>
        {loading ? <li className="text-xs text-slate-400 text-center py-3">Chargement…</li> : null}
        {!loading && error ? <li className="text-xs text-rose-700 text-center py-3">{error}</li> : null}
        {!loading && !error && !results.length ? (
          <li className="text-xs text-slate-500 text-center py-3">Aucune visualisation publiée. Composez-en une dans Lens, puis « Publier… ».</li>
        ) : null}
        {!loading && results.map((r) => (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => setSelected({ nid: r.id, label: r.title })}
              aria-pressed={selected?.nid === r.id}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${selected?.nid === r.id ? 'bg-iris-50 text-iris-900' : 'hover:bg-slate-50'}`}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate">{r.title}</span>
                <span className="block text-xs text-slate-500">
                  n° {r.id} · {r.index}
                  {r.params?.length ? ` · paramètres : ${r.params.map((p) => p.name).join(', ')}` : ''}
                </span>
              </span>
              {selected?.nid === r.id ? <Check size={14} className="text-iris-600 shrink-0" aria-hidden="true" /> : null}
            </button>
          </li>
        ))}
      </ul>

      {current?.params?.length ? (
        <p className="field-hint">
          Cette visualisation attend {current.params.length > 1 ? 'les paramètres' : 'le paramètre'}{' '}
          {current.params.map((p) => `« ${p.name} »${p.example ? ` (exemple ${p.example})` : ''}`).join(', ')} :
          renseignez-{current.params.length > 1 ? 'les' : 'le'} dans « Paramètres » du menu du bloc.
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {selected ? (
          <a className="btn btn-secondary btn-sm" href={`/lens?visualization=${selected.nid}`} target="_blank" rel="noopener noreferrer" data-builder-allow-navigation="">
            <ExternalLink size={14} aria-hidden="true" />Modifier dans Lens<span className="sr-only"> (nouvel onglet)</span>
          </a>
        ) : null}
        <a className="btn btn-ghost btn-sm" href="/lens" target="_blank" rel="noopener noreferrer" data-builder-allow-navigation="">
          <Plus size={14} aria-hidden="true" />Créer dans Lens<span className="sr-only"> (nouvel onglet)</span>
        </a>
      </div>

      <div className="builder-editor-actions builder-editor-actions-split">
        <button type="button" className="btn btn-sm btn-ghost builder-editor-remove" onClick={() => { onSave(null); onClose() }}>
          Retirer
        </button>
        <div className="flex gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="btn btn-primary" disabled={!selected} onClick={() => { onSave(selected); onClose() }}>
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}
